import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Check, X, Inbox, ShieldCheck } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api.js';
import { fmtFromNow } from '../../lib/format.js';

/**
 * Manager Incoming Sync Requests Review Card
 */
export default function ManagerPendingApprovals() {
  const qc = useQueryClient();
  const [busyId, setBusyId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['sync-pending'],
    queryFn: () => apiGet('/sync/pending'),
    refetchInterval: 10000,
  });

  const requests = data?.requests || [];

  const handleApprove = async (requestId, requesterName) => {
    setBusyId(requestId);
    try {
      const res = await apiPost('/sync/approve', { requestId });
      if (res?.ok) {
        toast.success(`Settings successfully synced to ${requesterName || 'Agent'}`);
        qc.invalidateQueries({ queryKey: ['sync-pending'] });
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || 'Approval failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleDecline = async (requestId) => {
    if (!confirm('Decline this sync request?')) return;
    setBusyId(requestId);
    try {
      const res = await apiPost('/sync/reject', { requestId });
      if (res?.ok) {
        toast('Sync request declined');
        qc.invalidateQueries({ queryKey: ['sync-pending'] });
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || 'Decline failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="panel p-5 space-y-4 border border-white/10 bg-[#0d0714] shadow-xl rounded-2xl">
      <div className="flex items-center justify-between pb-2 border-b border-white/5">
        <div className="flex items-center gap-2.5">
          <Inbox className="w-5 h-5 text-civix-purpleSoft" />
          <h3 className="font-display font-bold text-white text-base">
            Incoming Sync Requests
          </h3>
          {requests.length > 0 && (
            <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-amber-400 text-black">
              {requests.length}
            </span>
          )}
        </div>
        <span className="text-[11px] text-white/40 flex items-center gap-1 font-mono">
          <ShieldCheck className="w-3.5 h-3.5 text-civix-cyan" /> Manager Hub
        </span>
      </div>

      {isLoading && (
        <div className="py-8 text-center text-xs text-white/40">
          Loading incoming requests…
        </div>
      )}

      {!isLoading && requests.length === 0 && (
        <div className="py-10 text-center space-y-1">
          <div className="text-white/60 text-sm font-medium">No pending sync requests</div>
          <p className="text-white/40 text-xs max-w-xs mx-auto">
            When new agents on your team send a sync request to inherit your setup, they will appear here for review.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {requests.map((r) => {
          const requester = r.requesterId || r.fromAgent;
          const name = requester?.name || requester?.email || 'Downline Agent';
          const email = requester?.email || '';
          const isBusy = busyId === r._id;

          return (
            <div
              key={r._id}
              className="p-4 rounded-xl border border-white/10 bg-black/40 space-y-3 hover:border-white/20 transition"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-bold text-white text-sm">{name}</div>
                  <div className="text-xs text-white/50">{email}</div>
                  <div className="text-[11px] text-civix-cyan mt-1">
                    Requested {fmtFromNow(r.requestedAt || r.createdAt)}
                  </div>
                </div>
                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-500/15 text-amber-300 border border-amber-500/30 uppercase">
                  Pending
                </span>
              </div>

              {r.note && (
                <div className="text-xs p-2.5 rounded-lg bg-white/5 border border-white/5 text-white/70 italic">
                  "{r.note}"
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center gap-2 pt-1">
                <button
                  onClick={() => handleApprove(r._id, name)}
                  disabled={isBusy}
                  className="flex-1 py-2 px-3 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-xs transition flex items-center justify-center gap-1.5 shadow-sm cursor-pointer disabled:opacity-50"
                >
                  <Check className="w-3.5 h-3.5" />
                  {isBusy ? 'Syncing Templates…' : 'Approve & Sync Templates'}
                </button>
                <button
                  onClick={() => handleDecline(r._id)}
                  disabled={isBusy}
                  className="py-2 px-3 rounded-lg bg-white/10 hover:bg-rose-500/20 text-white/70 hover:text-rose-300 font-medium text-xs border border-white/10 transition flex items-center justify-center gap-1 cursor-pointer disabled:opacity-50"
                >
                  <X className="w-3.5 h-3.5" />
                  Decline
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
